<template>
  <v-layout column>
    <div class="page-header">
      <h1 class="title-screen">{{ adminText('organizationAll.emails_to_subscribers_1820e838', 'Emails to Subscribers') }}</h1>
      <div>
        <main-button depressed @click="navigateToCreateEmail">
          <img class="create-icon" src="@/assets/icons/theater-email-create.svg" alt="" />
          {{ adminText('organizationAll.create_email_a30dad3e', 'CREATE EMAIL') }}
        </main-button>
      </div>
    </div>

    <div v-if="loadError" role="alert" class="history-error">
      <span>{{ adminText('organizationAll.could_not_load_email_history_please_try_again_af1857d4', 'Could not load email history. Please try again.') }}</span>
      <main-button flat :loading="loading" :disabled="loading" @click="fetchEmails">{{ adminText('organizationAll.retry_0c4430e6', 'RETRY') }}</main-button>
    </div>

    <v-data-table
      v-else
      :headers="organizationHeaders(headers)"
      :items="items"
      :loading="loading"
      :rows-per-page-items="rowsPerPageItems"
      :rows-per-page-text="adminText('organizationAll.rows_per_page_79df325c', 'Rows per page:')"
      item-key="id"
      disable-initial-sort
      class="table-container"
    >
      <template v-slot:items="props">
        <tr class="clickable-row" @click="openEmail(props.item)">
          <td>
            <v-tooltip bottom max-width="360">
              <span slot="activator" tabindex="0" class="delivery-status" :class="props.item.statusClass" :aria-label="organizationText(props.item.statusLabel + '. ' + props.item.statusDescription)">
                {{ organizationText(props.item.statusLabel) }}
              </span>
              <span>{{ organizationText(props.item.statusDescription) }}</span>
            </v-tooltip>
          </td>
          <td>
            <div class="custom-type-style"><span>{{ organizationText(props.item.type) }}</span></div>
          </td>
          <td><span class="created-date">{{ props.item.createdDate }}</span><span>{{ props.item.createdTime }}</span></td>
          <td><span class="email-subject" :title="props.item.subject">{{ props.item.subject }}</span></td>
          <td>{{ props.item.recipients }}</td>
        </tr>
      </template>
      <template v-slot:no-data>
        <div class="text-center">{{ adminText('organizationAll.no_emails_have_been_sent_to_subscribers_yet_8e4ea845', 'No emails have been sent to subscribers yet') }}</div>
      </template>
    </v-data-table>
  </v-layout>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import moment from 'moment'
import { GET_THEATER_SUBSCRIPTION_EMAILS } from '@/graphql/email.graphql'
import { theaterOrganizationRouteNames } from '@/routing/routeNames/theaterOrganizerRouterNames'

export default {
  mixins: [OrganizationLocalization],
  name: 'theater-subscriptions-emails',
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      emails: [],
      loading: false,
      loadError: false,
      rowsPerPageItems: [10, 25, 50],
      headers: [
        { text: 'Status', value: 'status', align: 'left', sortable: false },
        { text: 'Type', value: 'type', align: 'left', sortable: false },
        { text: 'Created', value: 'createdAt', align: 'left', sortable: false },
        { text: 'Subject', value: 'subject', align: 'left', sortable: false },
        { text: 'Recipients', value: 'recipients', align: 'left', sortable: false },
      ],
    }
  },
  computed: {
    items() {
      const locale = this.isAdminLocalization ? this.$adminLocale() : moment.locale()
      const dateFormat = this.isAdminLocalization && locale === 'hr' ? 'D[.] MMMM YYYY' : 'D. MMMM YYYY'

      return this.emails.map(email => ({
        id: email.id,
        // The API stores creation time; do not present it as delivery time.
        statusLabel: email.theaterDeliverySummary ? email.theaterDeliverySummary.status : 'Sending',
        statusDescription: this.deliveryDescription(email.theaterDeliverySummary),
        statusClass: { 'delivery-failed': email.theaterDeliverySummary && email.theaterDeliverySummary.failed > 0 },
        type: email.type || 'Custom',
        createdDate: email.createdAt ? moment(email.createdAt).locale(locale).format(dateFormat) : '',
        createdTime: email.createdAt ? moment(email.createdAt).format('HH:mm') : '',
        subject: email.subject || '',
        recipients: this.recipientLabel(email),
      }))
    },
  },
  created() {
    this.fetchEmails()
  },
  methods: {
    deliveryDescription(summary) {
      if (!summary) return this.adminText('organizationAll.delivery_information_is_unavailable_the_outco_2267d676', 'Delivery information is unavailable. The outcome has not been confirmed.')
      if (summary.status === 'Draft') return this.adminText('organizationAll.no_sending_attempt_has_been_recorded_2b3fcb77', 'No sending attempt has been recorded.')
      const counts = this.adminText('organizationAll.sent_of_total_accepted_by_the_email_provider__66086620', '{{sent}} of {{total}} accepted by the email provider; {{failed}} confirmed failed; {{pending}} pending or sending; {{unknown}} unknown.', { sent: summary.sent, total: summary.total, failed: summary.failed, pending: summary.pending, unknown: summary.unknown })
      const retry = summary.failed ? this.adminText('organizationAll.only_confirmed_failures_can_be_retried_4e864b4c', ' Only confirmed failures can be retried.') : ''
      const uncertain = summary.unknown ? this.adminText('organizationAll.unknown_outcomes_need_support_review_and_must_a2ab95a1', ' Unknown outcomes need support review and must not be retried automatically.') : ''
      return `${counts}${retry}${uncertain}${this.adminText('organizationAll.provider_acceptance_does_not_confirm_inbox_de_20d3f7c7', ' Provider acceptance does not confirm inbox delivery.')}`
    },
    async fetchEmails() {
      this.loading = true
      try {
        const { data } = await this.$apollo.query({
          query: GET_THEATER_SUBSCRIPTION_EMAILS,
          variables: { organizationSlug: this.organizationSlug },
          fetchPolicy: 'no-cache',
        })
        this.emails = (data && data.getTheaterSubscriptionEmails) || []
        this.loadError = false
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    recipientLabel(email) {
      const recipient = email.emailRecipient
      if (!recipient || !recipient.recipientType) {
        return ''
      }

      return this.organizationRecipientLabel(recipient.recipientType)
    },
    navigateToCreateEmail() {
      this.$router.push({
        name: theaterOrganizationRouteNames.theaterOrganizationTheaterSubscribersMessage,
        params: { organizationSlug: this.organizationSlug },
      })
    },
    openEmail(item) {
      this.$router.push({
        name: theaterOrganizationRouteNames.theaterOrganizationTheaterSubscribersEmailDetails,
        params: { organizationSlug: this.organizationSlug, emailId: String(item.id) },
      })
    },
  },
}
</script>

<style scoped>
.history-error,
.page-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}
.page-header {
  justify-content: space-between;
  margin-bottom: 32px;
}
.title-screen {
  margin: 0;
  font-family: 'Raleway', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.03em;
  color: #ff4c00;
}
.page-header .main-button {
  margin: 0;
  height: 48px;
  padding: 12px 24px;
}
.create-icon {
  margin-right: 8px;
  flex-shrink: 0;
}
.delivery-status {
  cursor: help;
  border-bottom: 1px dotted currentColor;
}
.delivery-status:focus-visible {
  outline: 2px solid #0277bd;
  outline-offset: 3px;
}
.delivery-failed {
  color: #b42336;
  font-weight: 700;
}
.clickable-row {
  cursor: pointer;
}
.created-date {
  display: block;
}
.email-subject {
  display: block;
  max-width: 124px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.custom-type-style {
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 8px;
  background: #d9d9d9;
  color: #333;
}
.table-container {
  border: 2px solid #f5f5f5;
  border-radius: 0 0 8px 8px;
  min-width: 0;
}
.table-container >>> table.v-table {
  min-width: 740px;
  table-layout: fixed;
}
.table-container >>> table.v-table th,
.table-container >>> table.v-table td {
  padding: 12px 24px;
  height: auto;
  font-size: 16px;
  line-height: 1.5;
  color: #666;
  border-bottom: 1px solid #f5f5f5;
  white-space: normal;
  overflow-wrap: anywhere;
}
.table-container >>> table.v-table th {
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.015em;
  background: #f5f5f5;
}
.table-container >>> table.v-table thead tr {
  height: 44px;
}
.table-container >>> table.v-table th:nth-child(1) { width: 18.5%; }
.table-container >>> table.v-table th:nth-child(2) { width: 18%; }
.table-container >>> table.v-table th:nth-child(3),
.table-container >>> table.v-table th:nth-child(4) { width: 23.5%; }
.table-container >>> table.v-table th:nth-child(5) { width: 16.5%; }
.table-container >>> table.v-table thead tr.v-datatable__progress {
  height: 1px;
}
.table-container >>> .v-datatable__progress th {
  padding: 0;
  border: 0;
}
.table-container >>> .v-datatable__actions {
  flex-wrap: wrap;
  min-height: 40px;
}
.table-container >>> .v-datatable__actions .v-btn {
  width: 32px;
  height: 32px;
  margin: 4px;
}
@media (max-width: 600px) {
  .table-container >>> .v-datatable__actions__select {
    margin-right: 8px;
  }
  .table-container >>> .v-datatable__actions__pagination {
    margin: 0 8px;
  }
}
</style>
